<template>
  <div>
    <button
      @click="toggleLayer('base', $event)"
      class="el-button el-button--primary el-button--mini"
      style="background-color: grey; border-color: grey; margin-bottom: 11px;"
    >
    {{ i18nValues["standard"] }} 
    </button>
    <button
      @click="toggleLayer('satellite', $event)"
      class="el-button el-button--primary el-button--mini"
      style="background-color: grey; border-color: grey;"
    >
    {{ i18nValues["satellite"] }} 
    </button>
    <button
      @click="toggleLayer('topography', $event)"
      class="el-button el-button--primary el-button--mini"
      style="background-color: grey; border-color: grey;"
    >
    {{ i18nValues["relief"] }} 
    </button>
    <div
      :id="mapId"
      style="width: 100% !important; height: 100% !important;"
    ></div>
    <div ref="tooltip" class="ol-tooltip"></div>
  </div>
</template>

<script>
import { Feature, Map, View, Overlay } from "ol/index";
import { OSM, Vector as VectorSource, XYZ } from "ol/source";
import { Point, Polygon } from "ol/geom";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { useGeographic } from "ol/proj";
import { Style, Icon, Fill, Stroke } from "ol/style";
import { isEmpty as isExtentEmpty } from "ol/extent";
import { Draw, defaults as defaultInteractions, MouseWheelZoom, DoubleClickZoom, PinchZoom } from "ol/interaction";
import GeoJSON from "ol/format/GeoJSON";
import KML from "ol/format/KML";

export default {
  name: "Map",
  props: {
    field: {
      type: Object,
      default: () => ({}),
    },
    fieldLot: {
      type: Object,
      default: null,
    },
    fieldLotsList: {
      type: Array,
      default: null,
    },
    enableDrawing: {
      type: Boolean,
      default: false,
    },
    mapId: {
      type: String,
      default: "map",
    },
    showMarkers: {
      type: Boolean,
      default: true,
    },
  },
  created() {
    useGeographic();
  },
  mounted() {
    this.place = [-65.258006, -35.138403];

    // Define the base OSM layer
    this.baseLayer = new TileLayer({
      source: new OSM({
        maxZoom: 19, // OSM typically supports up to zoom 19
      }),
      visible: false, // Start with this layer hidden
    });

    // Initialize your vector layer for markers
    this.vector = new VectorLayer({
      source: new VectorSource(),
    });

    // Initialize drawing source and layer
    this.drawingSource = new VectorSource();
    this.drawingLayer = new VectorLayer({
      source: this.drawingSource,
      style: new Style({
        fill: new Fill({ color: "rgba(76,175,80,0.2)" }),
        stroke: new Stroke({ color: "#4caf50", width: 2 }),
      }),
    });

    // Define the satellite layer
    this.satelliteLayer = new TileLayer({
      source: new XYZ({
        url:
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        maxZoom: 19, // ArcGIS World Imagery supports up to zoom 19
        visible: true, // Start with this layer visible (satellite mode)
      }),
    });

    // Define the topography layer
    this.topographyLayer = new TileLayer({
      source: new XYZ({
        url: "https://a.tile.opentopomap.org/{z}/{x}/{y}.png",
        maxZoom: 17, // OpenTopoMap typically supports up to zoom 17
        visible: false, // Start with this layer hidden
      }),
    });

    // Initialize the map with all layers
    // Use a conservative maxZoom (17) to ensure tiles are always available
    // OpenTopoMap only supports up to 17, and we want to be safe
    const MAX_ZOOM = 17;
    const view = new View({
      projection: "EPSG:4326",
      center: this.place,
      zoom: 6,
      maxZoom: MAX_ZOOM, // Prevent zooming beyond what tile sources support
      constrainResolution: true, // Force zoom to integer levels
    });
    
    // Explicitly set maxZoom using the setter
    view.setMaxZoom(MAX_ZOOM);
    
    // Store view reference for later use
    this.view = view;
    
    // Override setZoom to always respect maxZoom - this catches ALL zoom operations
    const originalSetZoom = view.setZoom.bind(view);
    view.setZoom = function(zoom) {
      if (zoom !== null && zoom !== undefined) {
        const safeZoom = Math.min(zoom, MAX_ZOOM);
        return originalSetZoom(safeZoom);
      }
      return originalSetZoom(zoom);
    };
    
    // Override animate to respect maxZoom
    const originalAnimate = view.animate.bind(view);
    view.animate = function(opt_animationOptions) {
      if (opt_animationOptions && opt_animationOptions.zoom !== undefined) {
        opt_animationOptions.zoom = Math.min(opt_animationOptions.zoom, MAX_ZOOM);
      }
      return originalAnimate(opt_animationOptions);
    };
    
    // Override fit to always respect maxZoom
    const originalFit = view.fit.bind(view);
    view.fit = function(geometryOrExtent, opt_options) {
      const options = opt_options || {};
      if (options.maxZoom === undefined || options.maxZoom > MAX_ZOOM) {
        options.maxZoom = MAX_ZOOM;
      }
      return originalFit(geometryOrExtent, options);
    };
    
    // Add aggressive constraint listeners to catch any zoom that slips through
    const enforceMaxZoom = () => {
      const currentZoom = view.getZoom();
      if (currentZoom !== null && currentZoom !== undefined && currentZoom > MAX_ZOOM) {
        // Use requestAnimationFrame to ensure this runs after any zoom operation
        requestAnimationFrame(() => {
          const checkZoom = view.getZoom();
          if (checkZoom !== null && checkZoom !== undefined && checkZoom > MAX_ZOOM) {
            originalSetZoom(MAX_ZOOM);
          }
        });
        originalSetZoom(MAX_ZOOM);
      }
    };
    
    // Listen to all possible zoom-related events with immediate enforcement
    view.on('change:resolution', enforceMaxZoom);
    view.on('change:zoom', enforceMaxZoom);
    view.on('moveend', enforceMaxZoom);
    
    // Also add a more frequent check using requestAnimationFrame
    // Store the animation frame ID so we can cancel it if needed
    let continuousCheckFrameId = null;
    const continuousCheck = () => {
      const currentZoom = view.getZoom();
      if (currentZoom !== null && currentZoom !== undefined) {
        if (currentZoom > MAX_ZOOM) {
          originalSetZoom(MAX_ZOOM);
        }
      }
      continuousCheckFrameId = requestAnimationFrame(continuousCheck);
    };
    continuousCheckFrameId = requestAnimationFrame(continuousCheck);
    
    // Store references for cleanup
    this.continuousCheckFrameId = continuousCheckFrameId;
    this.MAX_ZOOM = MAX_ZOOM;
    this.originalSetZoom = originalSetZoom;
    this.enforceMaxZoom = enforceMaxZoom;
    
    // Configure zoom interactions to respect maxZoom
    const mouseWheelZoom = new MouseWheelZoom({
      constrainResolution: true,
    });
    
    const doubleClickZoom = new DoubleClickZoom({
      duration: 250,
    });
    
    const pinchZoom = new PinchZoom({
      constrainResolution: true,
    });
    
    this.map = new Map({
      target: this.mapId,
      view: view,
      layers: [
        this.satelliteLayer,
        this.topographyLayer,
        this.baseLayer,
        this.vector,
        this.drawingLayer,
      ],
      interactions: defaultInteractions({
        mouseWheelZoom: mouseWheelZoom,
        doubleClickZoom: doubleClickZoom,
        pinchZoom: pinchZoom,
      }),
    });
    
    // Also check after any map interaction
    this.map.on('moveend', enforceMaxZoom);

    this.tooltip = this.$refs.tooltip;

    this.tooltipOverlay = new Overlay({
      element: this.tooltip,
      positioning: "center-center",
    });
    this.map.addOverlay(this.tooltipOverlay);

    this.map.on("pointermove", (event) => {
      this.tooltipOverlay.setPosition(undefined);
      
      // Collect all features at this pixel
      const featuresAtPixel = [];
      this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
        featuresAtPixel.push(feature);
      });
      
      if (featuresAtPixel.length > 0) {
        // Prioritize variety location features over fieldLot features
        const varietyLocation = featuresAtPixel.find(f => f.get("isVarietyLocation"));
        const featureToShow = varietyLocation || featuresAtPixel[0];
        
        const name = featureToShow.get("name");
        if (name) {
          const coordinate = event.coordinate;
          this.tooltip.innerHTML = name;
          this.tooltipOverlay.setPosition(coordinate);
        }
      }
    });

    // Explicitly set layer visibility to ensure satellite is visible by default
    // Do this after map initialization but before rendering
    this.satelliteLayer.setVisible(true);
    this.baseLayer.setVisible(false);
    this.topographyLayer.setVisible(false);

    this.renderMapData();

    if (this.map) {
      this.$nextTick(() => {
        if (this.map.updateSize) {
          this.map.updateSize();
        }
        // Ensure visibility is set again after DOM update
        this.satelliteLayer.setVisible(true);
        this.baseLayer.setVisible(false);
        this.topographyLayer.setVisible(false);
      });
    }
  },
  data() {
    return {
      locations: [],
      drawInteraction: null,
      view: null,
      continuousCheckFrameId: null,
      MAX_ZOOM: 17,
      originalSetZoom: null,
      enforceMaxZoom: null,
    };
  },
  beforeDestroy() {
    // Clean up the continuous check animation frame
    if (this.continuousCheckFrameId !== null) {
      cancelAnimationFrame(this.continuousCheckFrameId);
      this.continuousCheckFrameId = null;
    }
  },
  methods: {
    // Helper method to safely set zoom, respecting maxZoom
    setZoomSafe(zoom) {
      if (this.map && this.map.getView()) {
        const maxZoom = 17; // Match the MAX_ZOOM constant
        const safeZoom = Math.min(zoom, maxZoom);
        this.map.getView().setZoom(safeZoom);
      }
    },
    toggleLayer(layerType, event) {
      // Check if event exists before calling methods on it
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      // Check if layers are initialized before trying to use them
      if (!this.baseLayer || !this.satelliteLayer || !this.topographyLayer) {
        // Layers not initialized yet, return early
        return;
      }

      // Hide all layers initially
      this.baseLayer.setVisible(false);
      this.satelliteLayer.setVisible(false);
      this.topographyLayer.setVisible(false);

      // Based on the layer type, make the respective layer visible
      switch (layerType) {
        case "base":
          this.baseLayer.setVisible(true);
          break;
        case "satellite":
          this.satelliteLayer.setVisible(true);
          break;
        case "topography":
          this.topographyLayer.setVisible(true);
          break;
      }
    },

    markFieldLots(fieldLots = []) {
      if (!Array.isArray(fieldLots)) {
        return;
      }
      fieldLots.forEach((fieldLot) => {
        this.addFeature(fieldLot);
      });
    },
    addMarkerForField(field) {
      const coordinate = this.parseLocation(field && field.location);
      if (!coordinate) {
        return false;
      }

      const marker = new Feature({
        geometry: new Point(coordinate),
        name: field.name,
      });

      marker.setStyle(
        new Style({
          image: new Icon({
            anchor: [0.5, 36],
            anchorXUnits: "fraction",
            anchorYUnits: "pixels",
            opacity: 1,
            scale: 1,
            src:
              "data:image/png;base64,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",
            zIndex: 2,
          }),
          zIndex: 2,
        }),
      );

      this.vector.getSource().addFeature(marker);
      return true;
    },
    addFeature(fieldLot) {
      if (!fieldLot) {
        return false;
      }
      let polygonGeometry = null;

      // Location now represents the full polygon geometry (boundaryGeometry)
      // Check location first, then fall back to boundaryGeometry for backward compatibility
      const geometryString = fieldLot.location || fieldLot.boundaryGeometry;
      if (geometryString && typeof geometryString === "string") {
        try {
          // Detect format from the string or use boundaryFormat
          let format = (fieldLot.boundaryFormat || "geojson").toLowerCase();
          if (!fieldLot.boundaryFormat || fieldLot.boundaryFormat === "geojson") {
            // Try to detect if it's KML
            if (geometryString.includes("<kml") || geometryString.includes("<?xml")) {
              format = "kml";
            } else {
              format = "geojson";
            }
          }
          
          let features = [];
          if (format === "geojson") {
            features = new GeoJSON().readFeatures(geometryString, {
              featureProjection: this.map.getView().getProjection(),
            });
          } else if (format === "kml") {
            features = new KML().readFeatures(geometryString, {
              featureProjection: this.map.getView().getProjection(),
            });
          }
          if (features && features.length) {
            features.forEach((feat) => {
              feat.set("name", fieldLot.name);
              feat.set("isFieldLotPolygon", true); // Mark as fieldLot polygon for easier identification
              if (fieldLot.id) {
                feat.set("fieldLotId", fieldLot.id); // Store ID for matching
              }
              feat.setStyle(
                new Style({
                  fill: new Fill({ color: "rgba(26,115,232,0.35)" }),
                  stroke: new Stroke({ color: "#1a73e8", width: 2 }),
                  zIndex: 1,
                })
              );
              this.vector.getSource().addFeature(feat);
            });
            const firstGeom = features[0] && features[0].getGeometry && features[0].getGeometry();
            if (firstGeom instanceof Polygon) {
              polygonGeometry = firstGeom;
            }
          }
        } catch (e) {
          // ignore malformed geometry strings and fallback to legacy array
        }
      }

      if (!polygonGeometry) {
        polygonGeometry = this.addPolygonFeature(fieldLot);
      }
      const markerCoordinate = this.resolveLotCoordinate(fieldLot, polygonGeometry);

      if (markerCoordinate && this.showMarkers) {
        this.addLotMarker(markerCoordinate, fieldLot.name);
      }

      return Boolean(polygonGeometry || markerCoordinate);
    },
    addPolygonFeature(fieldLot) {
      if (!Array.isArray(fieldLot.boundary) || fieldLot.boundary.length < 3) {
        return null;
      }

      const ring = fieldLot.boundary
        .map((pair) => this.normalizeBoundaryCoordinate(pair))
        .filter((pair) => Array.isArray(pair));

      if (ring.length < 3) {
        return null;
      }

      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push([first[0], first[1]]);
      }

      const polygon = new Polygon([ring]);
      const feature = new Feature({
        geometry: polygon,
        name: fieldLot.name,
      });

      feature.set("isFieldLotPolygon", true); // Mark as fieldLot polygon for easier identification
      if (fieldLot.id) {
        feature.set("fieldLotId", fieldLot.id); // Store ID for matching
      }
      feature.setStyle(
        new Style({
          fill: new Fill({ color: "rgba(26,115,232,0.35)" }),
          stroke: new Stroke({ color: "#1a73e8", width: 2 }),
          zIndex: 1,
        })
      );

      this.vector.getSource().addFeature(feature);
      return polygon;
    },
    addLotMarker(coordinate, name) {
      const marker = new Feature({
        geometry: new Point(coordinate),
        name,
      });

      marker.setStyle(
        new Style({
          image: new Icon({
            anchor: [0.5, 36],
            anchorXUnits: "fraction",
            anchorYUnits: "pixels",
            opacity: 1,
            scale: 0.5,
            src:
              "data:image/png;base64,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",
            zIndex: 3,
          }),
          zIndex: 3,
        })
      );

      this.vector.getSource().addFeature(marker);
    },
    normalizeBoundaryCoordinate(pair) {
      if (!Array.isArray(pair) || pair.length < 2) {
        return null;
      }

      const lat = parseFloat(pair[0]);
      const lon = parseFloat(pair[1]);

      if (!isFinite(lat) || !isFinite(lon)) {
        return null;
      }

      return [lon, lat];
    },
    resolveLotCoordinate(fieldLot, polygonGeometry) {
      // Location now represents full polygon geometry, not a simple coordinate
      // So we use the polygon's interior point for marker placement
      if (polygonGeometry) {
        try {
          return polygonGeometry.getInteriorPoint().getCoordinates();
        } catch (error) {
          return null;
        }
      }

      // Fallback: if location is still a simple coordinate (legacy data), try to parse it
      // But this should be rare now since location represents full geometry
      if (fieldLot && fieldLot.location) {
        // Only try to parse if it looks like a simple coordinate (not JSON/XML)
        const loc = fieldLot.location.trim();
        if (!loc.startsWith("{") && !loc.startsWith("<") && !loc.startsWith("[")) {
          const locationCoordinate = this.parseLocation(loc);
          if (locationCoordinate) {
            return locationCoordinate;
          }
        }
      }

      return null;
    },
    parseLocation(location) {
      if (!location) {
        return null;
      }

      try {
        const coordinates = JSON.parse(`[${location}]`);
        if (!Array.isArray(coordinates) || coordinates.length < 2) {
          return null;
        }

        const lat = parseFloat(coordinates[0]);
        const lon = parseFloat(coordinates[1]);

        if (!isFinite(lat) || !isFinite(lon)) {
          return null;
        }

        return [lon, lat];
      } catch (error) {
        return null;
      }
    },
    renderMapData() {
      if (!this.map || !this.vector) {
        return;
      }

      const source = this.vector.getSource();
      source.clear();

      let featuresAdded = false;
      let fieldCoordinate = null;

      // First, add the field marker and get its coordinate
      if (this.field && this.field.location) {
        fieldCoordinate = this.parseLocation(this.field.location);
        featuresAdded = this.addMarkerForField(this.field) || featuresAdded;
      }

      const activeLot = this.fieldLot;
      
      // If fieldLotsList is null (not provided), fall back to field.fieldLots
      // If fieldLotsList is an array (even if empty), use it exclusively
      const lotsFromProps = Array.isArray(this.fieldLotsList)
        ? this.fieldLotsList
        : null;
      const lotsFromField =
        this.field && Array.isArray(this.field.fieldLots)
          ? this.field.fieldLots
          : [];

      // Only use field.fieldLots if fieldLotsList was not explicitly provided (null)
      // If fieldLotsList is an array (even empty), use it and don't fall back
      const lotsToRender = lotsFromProps !== null ? lotsFromProps : lotsFromField;

      lotsToRender.forEach((lot) => {
        if (activeLot && activeLot.id && lot && lot.id === activeLot.id) {
          return;
        }
        featuresAdded = this.addFeature(lot) || featuresAdded;
      });

      if (activeLot) {
        featuresAdded = this.addFeature(activeLot) || featuresAdded;
      }

      if (featuresAdded) {
        // Prioritize active fieldLot if it exists, otherwise fit to all features
        this.fitToFeatures(fieldCoordinate, activeLot);
      } else if (fieldCoordinate) {
        // If no features but we have field location, center on it
        this.map.getView().setCenter(fieldCoordinate);
        this.setZoomSafe(15);
      }
    },
    fitToFeatures(fieldCoordinate = null, activeFieldLot = null) {
      if (!this.map || !this.vector) {
        return;
      }

      const source = this.vector.getSource();
      const features = source.getFeatures();

      if (!features.length) {
        // If no features but we have field coordinate, center on it
        if (fieldCoordinate) {
          this.map.getView().setCenter(fieldCoordinate);
          this.setZoomSafe(15);
        }
        return;
      }

      // If we have an active fieldLot, prioritize it by fitting only to its extent
      if (activeFieldLot && activeFieldLot.id) {
        // Find the feature that corresponds to the active fieldLot
        const activeFeature = features.find((feature) => {
          const geometry = feature.getGeometry();
          if (!geometry) return false;
          // Check if this is a fieldLot polygon feature
          const isFieldLotPolygon = feature.get("isFieldLotPolygon");
          if (!isFieldLotPolygon) return false;
          // Match by ID first (most reliable), then by name
          const featureId = feature.get("fieldLotId");
          if (featureId && activeFieldLot.id && featureId === activeFieldLot.id) {
            return true;
          }
          // Fallback to name matching
          const featureName = feature.get("name");
          return featureName === activeFieldLot.name;
        });

        if (activeFeature) {
          const geometry = activeFeature.getGeometry();
          if (geometry) {
            const extent = geometry.getExtent();
            if (extent && !isExtentEmpty(extent)) {
              this.map.getView().fit(extent, {
                padding: [50, 50, 50, 50],
                maxZoom: 17,
                duration: 250,
              });
              return;
            }
          }
        }
      }

      // Fallback: fit to all features
      const extent = source.getExtent();
      if (!extent || isExtentEmpty(extent)) {
        // If extent is empty but we have field coordinate, center on it
        if (fieldCoordinate) {
          this.map.getView().setCenter(fieldCoordinate);
          this.setZoomSafe(15);
        }
        return;
      }

      // If we have a field coordinate, prioritize it by expanding the extent to include it
      if (fieldCoordinate) {
        const fieldExtent = [
          Math.min(extent[0], fieldCoordinate[0]),
          Math.min(extent[1], fieldCoordinate[1]),
          Math.max(extent[2], fieldCoordinate[0]),
          Math.max(extent[3], fieldCoordinate[1]),
        ];
        this.map.getView().fit(fieldExtent, {
          padding: [50, 50, 50, 50],
          maxZoom: 17,
          duration: 250,
        });
      } else {
        this.map.getView().fit(extent, {
          padding: [50, 50, 50, 50],
          maxZoom: 17,
          duration: 250,
        });
      }
    },
    startDrawing() {
      if (this.drawInteraction) {
        this.map.removeInteraction(this.drawInteraction);
      }

      this.clearDraftPolygon();

      this.drawInteraction = new Draw({
        source: this.drawingSource,
        type: "Polygon",
        style: new Style({
          fill: new Fill({ color: "rgba(76,175,80,0.2)" }),
          stroke: new Stroke({ color: "#4caf50", width: 2 }),
        }),
      });

      this.drawInteraction.on("drawend", (event) => {
        const feature = event.feature;
        const geometry = feature.getGeometry();
        if (geometry instanceof Polygon) {
          // ensure closed ring
          let ring = geometry.getCoordinates()[0];
          if (ring && ring.length > 1) {
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
              ring = [...ring, first];
            }
          }
          feature.setGeometry(new Polygon([ring]));
          feature.set("isDraftPolygon", true);
          // Set green style for the new drawing
          feature.setStyle(
            new Style({
              fill: new Fill({ color: "rgba(76,175,80,0.2)" }),
              stroke: new Stroke({ color: "#4caf50", width: 2 }),
            })
          );

          // Export as GeoJSON string (primary format)
          // With useGeographic(), map projection is EPSG:4326, so we can write directly
          const geojson = new GeoJSON().writeFeature(feature, {
            featureProjection: this.map.getView().getProjection(),
          });

          this.$emit("polygon-drawn", {
            success: true,
            geojson,
          });
        }
      });

      this.map.addInteraction(this.drawInteraction);
    },
    stopDrawing() {
      if (this.drawInteraction) {
        this.map.removeInteraction(this.drawInteraction);
        this.drawInteraction = null;
      }
      const features = this.drawingSource.getFeatures();
      const draftFeatures = features.filter((f) => !f.get("isDraftPolygon"));
      draftFeatures.forEach((f) => this.drawingSource.removeFeature(f));
    },
    
    clearDraftPolygon() {
      const features = this.drawingSource.getFeatures();
      const draftFeatures = features.filter((f) => f.get("isDraftPolygon"));
      draftFeatures.forEach((f) => this.drawingSource.removeFeature(f));
    },
    
    clearCurrentGeometry() {
      // Clear only fieldLot polygon features from the vector layer, keep field markers and fieldLot markers
      if (this.vector && this.vector.getSource()) {
        const source = this.vector.getSource();
        const features = source.getFeatures().slice(); // Create a copy to avoid modification during iteration
        
        // Remove only Polygon features that are fieldLot polygons, keep Point features (markers)
        features.forEach((feature) => {
          const geometry = feature.getGeometry();
          // Only remove Polygon features (fieldLot boundaries), keep Point features (markers)
          if (geometry instanceof Polygon) {
            source.removeFeature(feature);
          }
        });
      }
    },
    
    loadGeometryString(format, text) {
      if (!text || !this.map || !this.vector) {
        // eslint-disable-next-line no-console
        console.error("loadGeometryString: Missing required parameters", { text: !!text, map: !!this.map, vector: !!this.vector });
        return false;
      }

      // Clear previous temporary drawings
      const drawingFeatures = this.drawingSource.getFeatures().slice();
      drawingFeatures.forEach((f) => this.drawingSource.removeFeature(f));

      const source = this.vector.getSource();
      
      // Preserve fieldLot features and field markers before clearing
      const existingFeatures = source.getFeatures().slice();
      const fieldLotFeatures = existingFeatures.filter((f) => {
        const isFieldLotPolygon = f.get("isFieldLotPolygon");
        const geometry = f.getGeometry();
        // Keep fieldLot polygons and field markers (Point features)
        return isFieldLotPolygon || (geometry && geometry.getType() === "Point");
      });
      
      source.clear();
      
      // Re-add preserved fieldLot features and markers
      if (fieldLotFeatures.length > 0) {
        source.addFeatures(fieldLotFeatures);
      }

      let features = [];
      try {
        const viewProj = this.map.getView().getProjection();
        // eslint-disable-next-line no-console
        console.log("Map view projection:", viewProj ? viewProj.getCode() : "unknown");
        
        if (format === "geojson") {
          // GeoJSON uses EPSG:4326 by default
          features = new GeoJSON().readFeatures(text, {
            dataProjection: "EPSG:4326",
            featureProjection: viewProj,
          });
        } else if (format === "kml") {
          // KML uses EPSG:4326 (WGS84) by default
          // Read without transformation - KML coordinates should match useGeographic()
          features = new KML().readFeatures(text, {
            dataProjection: "EPSG:4326",
            featureProjection: viewProj,
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error parsing geometry:", error);
        return false;
      }

      // eslint-disable-next-line no-console
      console.log("Parsed features:", features.length, format);

      if (features && features.length) {
        // Apply styling to loaded features and log geometry info
        features.forEach((feature, index) => {
          const geometry = feature.getGeometry();
          // eslint-disable-next-line no-console
          console.log(`Feature ${index}:`, {
            type: geometry ? geometry.getType() : "no geometry",
            coordinates: geometry ? geometry.getCoordinates() : null,
            extent: geometry ? geometry.getExtent() : null,
          });
          
          feature.setStyle(
            new Style({
              fill: new Fill({ color: "rgba(26,115,232,0.35)" }),
              stroke: new Stroke({ color: "#1a73e8", width: 2 }),
              zIndex: 1,
            })
          );
        });
        source.addFeatures(features);
        // eslint-disable-next-line no-console
        console.log("Features added to source. Source features count:", source.getFeatures().length);
        
        // Force map update and fit to features, prioritizing active fieldLot
        this.$nextTick(() => {
          if (this.map && this.map.updateSize) {
            this.map.updateSize();
          }
          // Pass activeFieldLot to ensure we zoom to it, not to other features
          this.fitToFeatures(null, this.fieldLot);
        });
        
        return true;
      } else {
        // eslint-disable-next-line no-console
        console.warn("No features extracted from", format, "file");
        return false;
      }
    },
    addVarietyLocation(geometryString, varietyName) {
      if (!geometryString || !this.map || !this.vector) {
        return false;
      }

      // Check if it's a geometry string (GeoJSON or KML)
      const isGeometryString = typeof geometryString === "string" && 
        (geometryString.trim().startsWith("{") || 
         geometryString.trim().startsWith("<"));
      
      if (!isGeometryString) {
        return false;
      }

      try {
        const source = this.vector.getSource();
        
        // Remove any existing variety locations first
        const existingFeatures = source.getFeatures();
        existingFeatures.forEach((feature) => {
          if (feature.get("isVarietyLocation")) {
            source.removeFeature(feature);
          }
        });
        
        const viewProj = this.map.getView().getProjection();
        
        // Detect format
        let format = "geojson";
        if (geometryString.includes("<kml") || geometryString.includes("<?xml")) {
          format = "kml";
        }

        let features = [];
        if (format === "geojson") {
          features = new GeoJSON().readFeatures(geometryString, {
            dataProjection: "EPSG:4326",
            featureProjection: viewProj,
          });
        } else if (format === "kml") {
          features = new KML().readFeatures(geometryString, {
            dataProjection: "EPSG:4326",
            featureProjection: viewProj,
          });
        }

        if (features && features.length) {
          features.forEach((feature) => {
            feature.set("name", varietyName || "Variedad");
            feature.set("isVarietyLocation", true); // Mark as variety location
            feature.setStyle(
              new Style({
                fill: new Fill({ color: "rgba(255,152,0,0.35)" }), // Orange color for variety
                stroke: new Stroke({ color: "#ff9800", width: 2 }),
                zIndex: 2, // Higher z-index to appear on top
              })
            );
            source.addFeature(feature);
          });
          return true;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error adding variety location:", error);
        return false;
      }
      return false;
    },
  },
  watch: {
    mapId: {
      handler() {
        if (this.map && this.mapId) {
          this.$nextTick(() => {
            const targetElement = document.getElementById(this.mapId);
            if (targetElement && this.map.getTarget() !== this.mapId) {
              this.map.setTarget(this.mapId);
            }
            if (this.map.updateSize) {
              setTimeout(() => {
                this.map.updateSize();
              }, 100);
            }
          });
        }
      },
      immediate: true,
    },
    enableDrawing(newVal) {
      if (newVal) {
        this.startDrawing();
      } else {
        this.stopDrawing();
      }
    },
    field: {
      deep: true,
      handler() {
        this.renderMapData();
      },
    },
    fieldLotsList: {
      deep: true,
      handler() {
        this.renderMapData();
      },
    },
    fieldLot: {
      deep: true,
      handler() {
        this.renderMapData();
      },
    },
  },
};
</script>

<style>
.ol-tooltip {
  position: absolute;
  background-color: #000;
  color: #fff;
  border-radius: 5px;
  padding: 6px 12px;
  white-space: nowrap;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
  opacity: 0.8;
}
</style>
